import React, { useState, useEffect } from 'react';
import { Eye } from 'lucide-react';
import { Calendar } from "../ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const Records = ({ axiosInstance }) => {
  const [activeTab, setActiveTab] = useState('stock-in');
  const [stockOutRecords, setStockOutRecords] = useState([]);
  const [stockInRecords, setStockInRecords] = useState([]);
  const [stockTransferRecords, setStockTransferRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  
  // Filter states
  const [dateFilter, setDateFilter] = useState(undefined);
  const [processedByFilter, setProcessedByFilter] = useState('');
  const [customerNameFilter, setCustomerNameFilter] = useState('');
  const [siNoFilter, setSiNoFilter] = useState('');
  const [drNoFilter, setDrNoFilter] = useState('');
  const [sourceWarehouseFilter, setSourceWarehouseFilter] = useState('');
  const [destinationWarehouseFilter, setDestinationWarehouseFilter] = useState('');
  
  // Mapping for UUID to Serial Number
  const [serialMap, setSerialMap] = useState({});

  useEffect(() => {
    // Fetch all machines and demo units to build a serial number map for resolving old UUIDs
    const fetchSerials = async () => {
      try {
        const [machinesRes, demoRes] = await Promise.all([
          axiosInstance.get('/stock/machines'),
          axiosInstance.get('/stock/demo-units')
        ]);
        
        const newMap = {};
        machinesRes.data.forEach(m => {
          if (m.id && m.serialNumber) newMap[m.id] = m.serialNumber;
        });
        demoRes.data.forEach(d => {
          if (d.id && d.serialNumber) newMap[d.id] = d.serialNumber;
        });
        
        setSerialMap(newMap);
      } catch (error) {
        console.error("Error fetching serials for mapping:", error);
      }
    };
    fetchSerials();
  }, []);

  useEffect(() => {
    // Clear filters on tab change
    setDateFilter(undefined);
    setProcessedByFilter('');
    setCustomerNameFilter('');
    setSiNoFilter('');
    setDrNoFilter('');
    setSourceWarehouseFilter('');
    setDestinationWarehouseFilter('');

    if (activeTab === 'stock-out') {
      fetchStockOutRecords();
    } else if (activeTab === 'stock-in') {
      fetchStockInRecords();
    } else if (activeTab === 'stock-transfer') {
      fetchStockTransferRecords();
    }
  }, [activeTab]);

  const fetchStockOutRecords = async () => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.get('/stock/out');
      setStockOutRecords(res.data);
    } catch (error) {
      console.error("Error fetching stock out records:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStockInRecords = async () => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.get('/stock/in');
      setStockInRecords(res.data);
    } catch (error) {
      console.error("Error fetching stock in records:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStockTransferRecords = async () => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.get('/stock/transfers');
      setStockTransferRecords(res.data);
    } catch (error) {
      console.error("Error fetching stock transfer records:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: '2-digit',
      year: 'numeric'
    });
  };

  // Helper function to group records
  const groupRecords = (records, type) => {
      const groups = {};

      records.forEach(record => {
          let key = '';
          const date = new Date(record.date || record.orderDate || record.transferDate).toDateString();
          
          if (type === 'stock-in') {
              const siKey = (record.si_number || '').trim().toLowerCase();
              const drKey = (record.dr_number || '').trim().toLowerCase();
              const supplierKey = (record.supplier || '').trim().toLowerCase();
              key = `${date}-${siKey}-${drKey}-${supplierKey}`;
          } else if (type === 'stock-out') {
              const processedBy = record.submittedBy || 'System';
              const customer = record.customerName || '';
              const siKey = (record.si_number || '').trim().toLowerCase();
              const drKey = (record.dr_number || '').trim().toLowerCase();
              key = `${processedBy}-${date}-${customer}-${siKey}-${drKey}`;
          } else if (type === 'stock-transfer') {
              const processedBy = record.transferredBy || 'System';
              const source = record.sourceWarehouseName || '';
              const dest = record.destinationWarehouseName || '';
              key = `${processedBy}-${date}-${source}-${dest}`;
          }

          if (!groups[key]) {
              groups[key] = { 
                  ...record, 
                  items: [...(record.items || [])],
                  totalItems: record.totalItems || (record.items ? record.items.length : 0)
              };
          } else {
              groups[key].items = [...groups[key].items, ...(record.items || [])];
              groups[key].totalItems += (record.totalItems || (record.items ? record.items.length : 0));
          }
      });

      return Object.values(groups);
  };

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setIsDetailOpen(true);
  };

  // Filter Logic
  const filteredStockOutRecords = groupRecords(stockOutRecords.filter(record => {
    // Date Filter
    if (dateFilter) {
      const recordDate = new Date(record.orderDate);
      if (
        recordDate.getDate() !== dateFilter.getDate() ||
        recordDate.getMonth() !== dateFilter.getMonth() ||
        recordDate.getFullYear() !== dateFilter.getFullYear()
      ) {
        return false;
      }
    }

    // Processed By Filter
    if (processedByFilter) {
      const submittedBy = record.submittedBy || 'System';
      if (!submittedBy.toLowerCase().includes(processedByFilter.toLowerCase())) {
        return false;
      }
    }

    // Customer Name Filter
    if (customerNameFilter) {
      if (!record.customerName.toLowerCase().includes(customerNameFilter.toLowerCase())) {
        return false;
      }
    }

    // SI No. Filter
    if (siNoFilter) {
      const siNumber = (record.si_number || '').toLowerCase();
      if (!siNumber.includes(siNoFilter.toLowerCase())) {
        return false;
      }
    }

    // DR No. Filter
    if (drNoFilter) {
      const drNumber = (record.dr_number || '').toLowerCase();
      if (!drNumber.includes(drNoFilter.toLowerCase())) {
        return false;
      }
    }

    return true;
  }), 'stock-out');

  const filteredStockInRecords = groupRecords(stockInRecords.filter(record => {
    // Date Filter
    if (dateFilter) {
      const recordDate = new Date(record.date);
      if (
        recordDate.getDate() !== dateFilter.getDate() ||
        recordDate.getMonth() !== dateFilter.getMonth() ||
        recordDate.getFullYear() !== dateFilter.getFullYear()
      ) {
        return false;
      }
    }

    // Processed By / SI / Supplier / Date Filter
    if (processedByFilter) {
      const searchTerm = processedByFilter.toLowerCase();
      const addedBy = (record.addedBy || 'System').toLowerCase();
      const siNumber = (record.si_number || '').toLowerCase();
      const drNumber = (record.dr_number || '').toLowerCase();
      const supplier = (record.supplier || '').toLowerCase();
      const deliveryDate = record.delivery_date ? new Date(record.delivery_date).toLocaleDateString().toLowerCase() : '';

      if (
        !addedBy.includes(searchTerm) &&
        !siNumber.includes(searchTerm) &&
        !drNumber.includes(searchTerm) &&
        !supplier.includes(searchTerm) &&
        !deliveryDate.includes(searchTerm)
      ) {
        return false;
      }
    }

    // Customer Name Filter (Not applicable for Stock-In, strictly filtering)
    if (customerNameFilter) {
      return false;
    }

    return true;
  }), 'stock-in');

  const filteredStockTransferRecords = groupRecords(stockTransferRecords.filter(record => {
    // Date Filter
    if (dateFilter) {
      const recordDate = new Date(record.transferDate);
      if (
        recordDate.getDate() !== dateFilter.getDate() ||
        recordDate.getMonth() !== dateFilter.getMonth() ||
        recordDate.getFullYear() !== dateFilter.getFullYear()
      ) {
        return false;
      }
    }

    // Processed By Filter
    if (processedByFilter) {
      const transferredBy = record.transferredBy || 'System';
      if (!transferredBy.toLowerCase().includes(processedByFilter.toLowerCase())) {
        return false;
      }
    }

    // Source Warehouse Filter
    if (sourceWarehouseFilter) {
      if (!record.sourceWarehouseName.toLowerCase().includes(sourceWarehouseFilter.toLowerCase())) {
        return false;
      }
    }

    // Destination Warehouse Filter
    if (destinationWarehouseFilter) {
      if (!record.destinationWarehouseName.toLowerCase().includes(destinationWarehouseFilter.toLowerCase())) {
        return false;
      }
    }

    return true;
  }), 'stock-transfer');

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Top Navigation Tabs - Centered */}
      <div className="bg-white border-b px-6 py-3 flex justify-center">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('stock-in')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'stock-in'
                ? 'bg-white text-gray-900 shadow-sm animate-beat'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200'
            }`}
          >
            Stock-In
          </button>
          <button
            onClick={() => setActiveTab('stock-out')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'stock-out'
                ? 'bg-white text-gray-900 shadow-sm animate-beat'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200'
            }`}
          >
            Stock-Out
          </button>
          <button
            onClick={() => setActiveTab('stock-transfer')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'stock-transfer'
                ? 'bg-white text-gray-900 shadow-sm animate-beat'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200'
            }`}
          >
            Stock-Transfer
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto p-6">
        <div className="animate-in fade-in zoom-in-95 duration-300">
          {activeTab === 'stock-in' && (
            <div className="flex gap-6 h-full">
                {/* Left Side - Filters (30%) */}
                <div className="w-[30%] space-y-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                        <Calendar
                            mode="single"
                            selected={dateFilter}
                            onSelect={setDateFilter}
                            className="rounded-md border shadow w-full flex justify-center"
                        />
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Search Filter
                            </label>
                            <input
                                type="text"
                                value={processedByFilter}
                                onChange={(e) => setProcessedByFilter(e.target.value)}
                                placeholder="Search Processed By, SI, DR, Supplier, Date..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        
                        {(dateFilter || processedByFilter || customerNameFilter) && (
                            <button
                                onClick={() => {
                                    setDateFilter(undefined);
                                    setProcessedByFilter('');
                                    setCustomerNameFilter('');
                                }}
                                className="w-full px-4 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                            >
                                Clear Filters
                            </button>
                        )}
                    </div>
                </div>

                {/* Right Side - Table (70%) */}
                <div className="w-[70%] bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                        <h3 className="text-lg font-medium text-gray-900">Stock-In History</h3>
                    </div>
                    
                    {isLoading ? (
                        <div className="p-8 text-center text-gray-500">Loading records...</div>
                    ) : filteredStockInRecords.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">No stock-in records found.</div>
                    ) : (
                        <div className="overflow-x-auto flex-1">
                            <table className="w-full text-sm text-left">
                                <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b">
                                    <tr>
                                        <th className="px-6 py-3">SI No.</th>
                                        <th className="px-6 py-3">DR No.</th>
                                        <th className="px-6 py-3">Supplier</th>
                                        <th className="px-6 py-3">Delivery Date</th>
                                        <th className="px-6 py-3">Date Created</th>
                                        <th className="px-6 py-3">Processed By</th>
                                        <th className="px-6 py-3 text-center">Total Items</th>
                                        <th className="px-6 py-3 text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredStockInRecords.map((record) => (
                                        <tr key={record.id} className="bg-white border-b hover:bg-gray-50">
                                            <td className="px-6 py-4 font-medium text-gray-900">
                                                {record.si_number || '-'}
                                            </td>
                                            <td className="px-6 py-4 font-medium text-gray-900">
                                                {record.dr_number || '-'}
                                            </td>
                                            <td className="px-6 py-4 text-gray-700">
                                                {record.supplier || '-'}
                                            </td>
                                            <td className="px-6 py-4 text-gray-700">
                                                {record.delivery_date ? new Date(record.delivery_date).toLocaleDateString() : '-'}
                                            </td>
                                            <td className="px-6 py-4 font-medium text-gray-900">
                                                {formatDate(record.date)}
                                            </td>
                                            <td className="px-6 py-4 text-gray-700">
                                                {record.addedBy || 'System'}
                                            </td>
                                            <td className="px-6 py-4 text-center text-gray-700">
                                                {record.totalItems}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <button 
                                                    onClick={() => handleViewDetails(record)}
                                                    className="text-blue-600 hover:text-blue-900 flex items-center justify-center gap-1 mx-auto"
                                                >
                                                    <Eye size={16} /> View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
          )}

          {activeTab === 'stock-out' && (
            <div className="flex gap-6 h-full">
                {/* Left Side - Filters (30%) */}
                <div className="w-[30%] space-y-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                        <Calendar
                            mode="single"
                            selected={dateFilter}
                            onSelect={setDateFilter}
                            className="rounded-md border shadow w-full flex justify-center"
                        />
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Processed By
                            </label>
                            <input
                                type="text"
                                value={processedByFilter}
                                onChange={(e) => setProcessedByFilter(e.target.value)}
                                placeholder="Search processed by..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                SI No.
                            </label>
                            <input
                                type="text"
                                value={siNoFilter}
                                onChange={(e) => setSiNoFilter(e.target.value)}
                                placeholder="Search SI no..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                DR No.
                            </label>
                            <input
                                type="text"
                                value={drNoFilter}
                                onChange={(e) => setDrNoFilter(e.target.value)}
                                placeholder="Search DR no..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Customer Name
                            </label>
                            <input
                                type="text"
                                value={customerNameFilter}
                                onChange={(e) => setCustomerNameFilter(e.target.value)}
                                placeholder="Search customer..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        
                        {(dateFilter || processedByFilter || customerNameFilter || siNoFilter || drNoFilter) && (
                            <button
                                onClick={() => {
                                    setDateFilter(undefined);
                                    setProcessedByFilter('');
                                    setSiNoFilter('');
                                    setDrNoFilter('');
                                    setCustomerNameFilter('');
                                }}
                                className="w-full px-4 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                            >
                                Clear Filters
                            </button>
                        )}
                    </div>
                </div>

                {/* Right Side - Table (70%) */}
                <div className="w-[70%] bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                        <h3 className="text-lg font-medium text-gray-900">Stock-Out History</h3>
                    </div>
                    
                    {isLoading ? (
                        <div className="p-8 text-center text-gray-500">Loading records...</div>
                    ) : filteredStockOutRecords.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">No stock-out records found.</div>
                    ) : (
                        <div className="overflow-x-auto flex-1">
                            <table className="w-full text-sm text-left">
                                <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b">
                                    <tr>
                                        <th className="px-6 py-3">Processed By</th>
                                        <th className="px-6 py-3">SI No.</th>
                                        <th className="px-6 py-3">DR No.</th>
                                        <th className="px-6 py-3">Date</th>
                                        <th className="px-6 py-3">Customer Name</th>
                                        <th className="px-6 py-3 text-center">Total Items</th>
                                        <th className="px-6 py-3 text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredStockOutRecords.map((record) => (
                                        <tr key={record.id} className="bg-white border-b hover:bg-gray-50">
                                            <td className="px-6 py-4 text-gray-700">
                                                {record.submittedBy || 'System'}
                                            </td>
                                            <td className="px-6 py-4 font-medium text-gray-900">
                                                {record.si_number || '-'}
                                            </td>
                                            <td className="px-6 py-4 font-medium text-gray-900">
                                                {record.dr_number || '-'}
                                            </td>
                                            <td className="px-6 py-4 font-medium text-gray-900">
                                                {formatDate(record.orderDate)}
                                            </td>
                                            <td className="px-6 py-4 text-gray-700">
                                                {record.customerName}
                                            </td>
                                            <td className="px-6 py-4 text-center text-gray-700">
                                                {record.items.length}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <button 
                                                    onClick={() => handleViewDetails(record)}
                                                    className="text-blue-600 hover:text-blue-900 flex items-center justify-center gap-1 mx-auto"
                                                >
                                                    <Eye size={16} /> View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
          )}

          {activeTab === 'stock-transfer' && (
            <div className="flex gap-6 h-full">
                {/* Left Side - Filters (30%) */}
                <div className="w-[30%] space-y-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                        <Calendar
                            mode="single"
                            selected={dateFilter}
                            onSelect={setDateFilter}
                            className="rounded-md border shadow w-full flex justify-center"
                        />
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Processed By
                            </label>
                            <input
                                type="text"
                                value={processedByFilter}
                                onChange={(e) => setProcessedByFilter(e.target.value)}
                                placeholder="Search processed by..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Source Warehouse
                            </label>
                            <input
                                type="text"
                                value={sourceWarehouseFilter}
                                onChange={(e) => setSourceWarehouseFilter(e.target.value)}
                                placeholder="Search source warehouse..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Destination Warehouse
                            </label>
                            <input
                                type="text"
                                value={destinationWarehouseFilter}
                                onChange={(e) => setDestinationWarehouseFilter(e.target.value)}
                                placeholder="Search destination warehouse..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        
                        {(dateFilter || processedByFilter || sourceWarehouseFilter || destinationWarehouseFilter) && (
                            <button
                                onClick={() => {
                                    setDateFilter(undefined);
                                    setProcessedByFilter('');
                                    setSourceWarehouseFilter('');
                                    setDestinationWarehouseFilter('');
                                }}
                                className="w-full px-4 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                            >
                                Clear Filters
                            </button>
                        )}
                    </div>
                </div>

                {/* Right Side - Table (70%) */}
                <div className="w-[70%] bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                        <h3 className="text-lg font-medium text-gray-900">Stock-Transfer History</h3>
                    </div>
                    
                    {isLoading ? (
                        <div className="p-8 text-center text-gray-500">Loading records...</div>
                    ) : filteredStockTransferRecords.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">No stock-transfer records found.</div>
                    ) : (
                        <div className="overflow-x-auto flex-1">
                            <table className="w-full text-sm text-left">
                                <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b">
                                    <tr>
                                        <th className="px-6 py-3">Processed By</th>
                                        <th className="px-6 py-3">Date</th>
                                        <th className="px-6 py-3">Source Warehouse</th>
                                        <th className="px-6 py-3">Destination Warehouse</th>
                                        <th className="px-6 py-3 text-center">Total Items</th>
                                        <th className="px-6 py-3 text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredStockTransferRecords.map((record) => (
                                        <tr key={record.id} className="bg-white border-b hover:bg-gray-50">
                                            <td className="px-6 py-4 text-gray-700">
                                                {record.transferredBy || 'System'}
                                            </td>
                                            <td className="px-6 py-4 font-medium text-gray-900">
                                                {formatDate(record.transferDate)}
                                            </td>
                                            <td className="px-6 py-4 text-gray-700">
                                                {record.sourceWarehouseName}
                                            </td>
                                            <td className="px-6 py-4 text-gray-700">
                                                {record.destinationWarehouseName}
                                            </td>
                                            <td className="px-6 py-4 text-center text-gray-700">
                                                {record.totalItems}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <button 
                                                    onClick={() => handleViewDetails(record)}
                                                    className="text-blue-600 hover:text-blue-900 flex items-center justify-center gap-1 mx-auto"
                                                >
                                                    <Eye size={16} /> View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
          )}
        </div>
      </div>

      {/* Transaction Details Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-[90vw] w-full max-h-[90vh] overflow-y-auto">
            <DialogHeader>
                <DialogTitle>Transaction Details</DialogTitle>
            </DialogHeader>
            {selectedRecord && (
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        {activeTab === 'stock-out' && (
                            <div>
                                <span className="font-semibold text-gray-500">Customer:</span>
                                <span className="ml-2 text-gray-900">{selectedRecord.customerName || '-'}</span>
                            </div>
                        )}
                        {activeTab === 'stock-transfer' && (
                            <>
                                <div>
                                    <span className="font-semibold text-gray-500">Source:</span>
                                    <span className="ml-2 text-gray-900">{selectedRecord.sourceWarehouseName}</span>
                                </div>
                                <div>
                                    <span className="font-semibold text-gray-500">Destination:</span>
                                    <span className="ml-2 text-gray-900">{selectedRecord.destinationWarehouseName}</span>
                                </div>
                            </>
                        )}
                        {activeTab === 'stock-in' && (
                            <>
                                <div>
                                    <span className="font-semibold text-gray-500">SI No.:</span>
                                    <span className="ml-2 text-gray-900">{selectedRecord.si_number || '-'}</span>
                                </div>
                                <div>
                                    <span className="font-semibold text-gray-500">DR No.:</span>
                                    <span className="ml-2 text-gray-900">{selectedRecord.dr_number || '-'}</span>
                                </div>
                                <div>
                                    <span className="font-semibold text-gray-500">Supplier:</span>
                                    <span className="ml-2 text-gray-900">{selectedRecord.supplier || '-'}</span>
                                </div>
                                <div>
                                    <span className="font-semibold text-gray-500">Delivery Date:</span>
                                    <span className="ml-2 text-gray-900">{selectedRecord.delivery_date ? new Date(selectedRecord.delivery_date).toLocaleDateString() : '-'}</span>
                                </div>
                            </>
                        )}
                        <div>
                            <span className="font-semibold text-gray-500">Date:</span>
                            <span className="ml-2 text-gray-900">
                                {formatDate(selectedRecord.orderDate || selectedRecord.date || selectedRecord.transferDate)}
                            </span>
                        </div>
                    </div>

                    <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm text-left">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b">
                                <tr>
                                    <th className="px-4 py-2">Type</th>
                                    <th className="px-4 py-2">Product</th>
                                    <th className="px-4 py-2">Description</th>
                                    <th className="px-4 py-2">Item Code / Part Number</th>
                                    <th className="px-4 py-2">Serial No.</th>
                                    <th className="px-4 py-2 text-right">Qty</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedRecord.items.map((item, idx) => (
                                    <tr key={idx} className="bg-white border-b last:border-0">
                                        <td className="px-4 py-2 text-gray-600">{item.lot || item.category || item.type}</td>
                                        <td className="px-4 py-2 font-medium text-gray-900">
                                            {item.brand} {item.model}
                                        </td>
                                        <td className="px-4 py-2 text-gray-600">{item.itemDescription}</td>
                                        <td className="px-4 py-2 text-gray-600">
                                            {item.itemCode || item.partNumber || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-gray-600">
                                            {(() => {
                                                let serials = [];
                                                if (Array.isArray(item.serial_ids) && item.serial_ids.length > 0) {
                                                    serials = item.serial_ids;
                                                } else if (Array.isArray(item.serialNumber)) {
                                                    serials = item.serialNumber;
                                                } else if (typeof item.serialNumber === 'string' && item.serialNumber.trim()) {
                                                    serials = item.serialNumber.split(',').map(s => s.trim()).filter(Boolean);
                                                } else if (typeof item.serial_ids === 'string' && item.serial_ids.trim()) {
                                                    serials = item.serial_ids.split(',').map(s => s.trim()).filter(Boolean);
                                                }
                                                
                                                // Resolve UUIDs using the fetched serialMap
                                                serials = serials.map(s => {
                                                    const isUUID = s.length === 36 && s.includes('-');
                                                    if (isUUID && serialMap[s]) {
                                                        return serialMap[s];
                                                    }
                                                    return isUUID ? "Serial Recorded in System" : s;
                                                });
                                                
                                                if (serials.length === 0) return '-';
                                                
                                                return (
                                                    <div className="flex flex-col">
                                                        {serials.map((s, i) => (
                                                            <div key={i}>{s}</div>
                                                        ))}
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td className="px-4 py-2 text-right font-medium text-gray-900">
                                            {item.orderQuantity || item.quantity}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Records;
